import { useEffect, useRef } from "react";
import moment from "moment-timezone";
import { useIntl } from "react-intl";
import ISupplier_Delivery_Schedule from "../../../models/ISupplier_Delivery_Schedule";
import ISupplier_Delivery_Schedule_Override from "../../../models/ISupplier_Delivery_Schedule_Override";
import {
    DAY_ABBR_KEYS,
    DEFAULT_DELIVERY_TIMEZONE,
    format12h,
    getDeliveryOptionCount,
    getUpcomingDeliveries,
    INextDeliveryResult,
    sortDays,
    toDeliveryDateKey,
} from "../../business/BusinessSettings/DeliveryDeadlines/deliveryDeadlineUtils";
import "./DeliveryDeadlineInfo.scss";

type Props = {
    schedule?: ISupplier_Delivery_Schedule | null;
    timezone?: string | null;
    /** Supplier holiday dates (YYYY-MM-DD) to roll past, as attached to the cart/order payload. */
    blackoutDates?: string[] | null;
    /** Holiday-week schedule exceptions for the vendor's schedule, as attached to the cart/order payload. */
    overrides?: ISupplier_Delivery_Schedule_Override[] | null;
    /** The delivery date currently chosen for the cart/order (YYYY-MM-DD or an ISO UTC-midnight timestamp). */
    selectedDate?: string | Date | null;
    /** When provided and the schedule allows customer choice, the upcoming dates render as a picker. */
    onSelectDate?: (deliveryDate: string) => void;
    /** Disables the picker (e.g. while a save is in flight or the order is read-only). */
    isReadOnly?: boolean;
};

/**
 * Read-only summary of a vendor's delivery schedule for the order/checkout Ship To
 * area: the configured delivery windows, the next expected delivery date, and the
 * current hard deadline. When the schedule lets customers choose a delivery day and the
 * caller passes onSelectDate, the next N open delivery dates render as a radio picker instead.
 * Date math runs in the vendor/supplier timezone (falls back to DEFAULT_DELIVERY_TIMEZONE,
 * same as the API). Renders nothing when the vendor has no schedule days.
 */
export const DeliveryDeadlineInfo = (props: Props) => {
    const intl = useIntl();

    const days = sortDays(props.schedule?.Days);
    const hasDeliveries = days.length > 0 || !!props.overrides?.some((override) => !override.no_delivery && override.Days?.length);

    const tz = props.timezone || DEFAULT_DELIVERY_TIMEZONE;
    const nowTz = moment.tz(tz);
    const canChoose = !!props.onSelectDate && getDeliveryOptionCount(props.schedule) > 1;
    const options = getUpcomingDeliveries(
        props.schedule,
        tz,
        canChoose ? getDeliveryOptionCount(props.schedule) : 1,
        props.blackoutDates,
        undefined,
        props.overrides
    );

    // The chosen date wins when it is still one of the offered dates; otherwise the soonest is shown as selected.
    const selectedKey = toDeliveryDateKey(props.selectedDate);
    const selected: INextDeliveryResult | undefined =
        options.find((option) => option.deliveryDate.format("YYYY-MM-DD") === selectedKey) || options[0];
    const fallbackKey = selected ? selected.deliveryDate.format("YYYY-MM-DD") : null;

    // A date that was chosen earlier but is no longer offered (its deadline passed, or a holiday /
    // exception moved it) is shown as the soonest date above; persist that so the cart or order
    // carries what is displayed. Once the parent stores it, selectedKey === fallbackKey and this stops.
    // The latest callback lives in a ref (callers recreate it every render) and each stale/fallback
    // pair is persisted once, so parent re-renders while the save is in flight cannot resend it.
    // The ref is written after commit (effects run in declaration order, so it is current before the
    // reconciliation effect below) rather than during render, which React may replay or discard.
    const onSelectDateRef = useRef(props.onSelectDate);
    const { onSelectDate, isReadOnly } = props;
    useEffect(() => {
        onSelectDateRef.current = onSelectDate;
    }, [onSelectDate]);
    const reconciledRef = useRef<string | null>(null);
    useEffect(() => {
        if (!selectedKey || !fallbackKey || selectedKey === fallbackKey || !onSelectDateRef.current) {
            reconciledRef.current = null;
            return;
        }

        // Nothing is sent while read-only (a save in flight, a read-only order), but the guard is kept
        // so the same stale pair is not resent when the picker re-enables after a failed save.
        if (isReadOnly) {
            return;
        }

        const pair = `${selectedKey}:${fallbackKey}`;
        if (reconciledRef.current === pair) {
            return;
        }

        reconciledRef.current = pair;
        onSelectDateRef.current(fallbackKey);
    }, [selectedKey, fallbackKey, isReadOnly]);

    // Hooks above run on every render; nothing to show without a template day or an exception date.
    if (!hasDeliveries) {
        return null;
    }

    // All configured delivery days, e.g. "Sun, Wed".
    const windows = days.map((d) => intl.formatMessage({ id: DAY_ABBR_KEYS[d.delivery_day_of_week] })).join(", ");

    // Zone abbreviation (e.g. CDT) so buyers in other timezones read the cutoff correctly.
    const deadlineLabel = (option: INextDeliveryResult) =>
        `${option.hardDeadline.format("ddd, MMM D")} ${format12h(option.hardDeadlineTime)} ${option.hardDeadline.format("z")}`;

    // Dates that come from a holiday-week exception are flagged so the change from the usual day is explained.
    const exceptionBadge = (option: INextDeliveryResult) =>
        option.isOverride ? (
            <span className="badge bg-warning text-dark ms-1">{intl.formatMessage({ id: "holiday_schedule" })}</span>
        ) : null;

    // Flag deadlines that are within a day so staff/buyers can act now.
    const isUrgent = (option: INextDeliveryResult) => option.hardDeadline.diff(nowTz, "hours") <= 24;

    const deliveryText = selected ? selected.deliveryDate.format("ddd, MMM D") : intl.formatMessage({ id: "delivery_deadline_passed" });
    const deadlineText = selected ? deadlineLabel(selected) : "—";

    return (
        <div className="DeliveryDeadlineInfo rounded p-2 mb-2 small">
            <div className="fw-bold mb-1">
                <i className="fas fa-truck me-1" />
                {intl.formatMessage({ id: "delivery_schedule" })}
            </div>
            <div className="d-flex flex-column">
                {canChoose && options.length > 1 ? (
                    <div className="mb-1" role="radiogroup" aria-label={intl.formatMessage({ id: "choose_delivery_day" })}>
                        <div className="fw-bold">{intl.formatMessage({ id: "choose_delivery_day" })}:</div>
                        {options.map((option) => {
                            const key = option.deliveryDate.format("YYYY-MM-DD");
                            const isChecked = selected === option;

                            return (
                                <div className="form-check" key={key}>
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="delivery_date"
                                        id={`delivery_date_${key}`}
                                        value={key}
                                        checked={isChecked}
                                        disabled={!!props.isReadOnly}
                                        onChange={() => props.onSelectDate && props.onSelectDate(key)}
                                    />
                                    <label className="form-check-label" htmlFor={`delivery_date_${key}`}>
                                        <span className="fw-bold">{option.deliveryDate.format("ddd, MMM D")}</span>
                                        {exceptionBadge(option)}
                                        <span className={isUrgent(option) ? "delivery-deadline-urgent fw-bold ms-1" : "text-muted ms-1"}>
                                            ({intl.formatMessage({ id: "order_by" })} {deadlineLabel(option)})
                                        </span>
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <>
                        <div>
                            <span className="fw-bold">{intl.formatMessage({ id: "next_expected_delivery" })}: </span>
                            <span>{deliveryText}</span>
                            {selected ? exceptionBadge(selected) : null}
                        </div>
                        <div className={selected && isUrgent(selected) ? "delivery-deadline-urgent fw-bold" : ""}>
                            <span className="fw-bold">{intl.formatMessage({ id: "hard_deadline" })}: </span>
                            <span>{deadlineText}</span>
                        </div>
                    </>
                )}
                <div>
                    <span className="fw-bold">{intl.formatMessage({ id: "delivery_windows" })}: </span>
                    <span>{windows}</span>
                </div>
            </div>
        </div>
    );
};

export default DeliveryDeadlineInfo;
